(() => {
  // V16 keeps its ordinary native cart. V17 also synchronizes each configured
  // wall with its optional fixing line; it never changes prices or clears carts.
  const script = document.querySelector('[data-artehub-native-cart-bridge]');
  if (!script) return;
  const root = script.dataset.storefrontRoot || '/';
  let refreshTimer;
  let drawerUpdate = 0;
  let headerUpdate = 0;
  // Storefront UX guard only. Shopify checkout still needs server-side validation
  // (or the existing order verification) against direct/API/other-channel edits.
  const originalFetch = window.fetch.bind(window);
  const VERSION = 'artehub-original-product-cart-v1';
  const LOCK = 'artehub-native-configuration-add';
  const FAILURE = 'تعذر تأكيد تحديث السلة. أعد المحاولة قبل المتابعة للدفع.';
  let displayedCart, dispatchIntent, queue = Promise.resolve(), pending = 0;
  let cartFailure = false, checkoutPending = false;
  const intents = new WeakMap();
  let initialState = 'pending', resolveInitial;
  const initialReady = new Promise(resolve => { resolveInitial = resolve; });
  const canonical = value => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
  const identity = item => item && ({ key: item.key, variant: String(item.variant_id), properties: canonical(item.properties),
    plan: String(item.selling_plan_allocation?.selling_plan?.id || '') });
  const same = (item, target) => target && String(item.variant_id) === target.variant && canonical(item.properties) === target.properties &&
    String(item.selling_plan_allocation?.selling_plan?.id || '') === target.plan;
  const managed = item => item?.properties?._original_cart_version === VERSION;
  const hasManaged = () => displayedCart?.items?.some(managed);
  const groupKey = item => JSON.stringify([item.properties._configuration_id, item.properties._customization_id]);
  function groups(cart) {
    const result = new Map();
    for (const item of cart.items) {
      if (!managed(item)) continue;
      const id = item.properties._configuration_id, component = item.properties._component;
      if (!/^[a-f0-9]{64}$/.test(id || '') || !/^ARTE-[A-Za-z0-9_-]{1,128}$/.test(item.properties._customization_id || '') || !['wallpaper', 'installation'].includes(component) ||
          !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 1000 || typeof item.key !== 'string' || !item.key) throw Error(FAILURE);
      const key = groupKey(item), group = result.get(key) || {};
      if (group[component]) throw Error(FAILURE); // Never guess between split/ambiguous lines.
      group[component] = item; result.set(key, group);
    }
    return result;
  }
  function announceFailure() {
    cartFailure = true;
    // Keep the last confirmed view stable after rejected/uncertain writes. This
    // is a local input rollback, never a compensating Shopify mutation.
    for (const row of document.querySelectorAll('[id^="CartItem-"], [id^="CartDrawer-Item-"]')) {
      const input = row.querySelector('input[name="updates[]"]');
      if (!input) continue;
      const matches = displayedCart?.items?.filter(item => same(item, intents.get(row))) || [];
      input.value = matches.length === 1 ? String(matches[0].quantity) : input.getAttribute('value');
    }
    const containers = [...document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors')];
    if (!containers.length) {
      const error = document.createElement('p'); error.dataset.artehubCartError = ''; error.setAttribute('role', 'alert');
      (document.querySelector('cart-items, cart-drawer-items') || document.body).append(error); containers.push(error);
    }
    for (const node of containers) { node.textContent = FAILURE; node.dataset.artehubCartError = ''; }
  }
  function clearFailure() {
    cartFailure = false;
    for (const node of document.querySelectorAll('[data-artehub-cart-error]')) node.textContent = '';
  }
  function bindRows() {
    if (!displayedCart?.items) return;
    for (const row of document.querySelectorAll('[id^="CartItem-"], [id^="CartDrawer-Item-"]')) {
      if (intents.has(row)) continue; // An old row keeps its identity until its section is replaced.
      const index = Number(row.id.match(/(\d+)$/)?.[1]) - 1;
      const item = displayedCart.items[index];
      if (!item) continue;
      // The section index is used only to bind a rendered row, never as the
      // mutation identifier. Queued edits re-resolve immutable properties.
      intents.set(row, identity(item));
      const fixing = managed(item) && item.properties._component === 'installation';
      if (fixing) {
        const input = row.querySelector('input[name="updates[]"]');
        if (input) { input.readOnly = true; input.setAttribute('aria-label', 'كمية التركيب تتبع عدد نسخ ورق الحائط'); }
        for (const button of row.querySelectorAll('quantity-input button')) button.disabled = true;
      }
    }
  }
  function remember(cart) { if (Array.isArray(cart?.items)) { displayedCart = cart; bindRows(); } }
  const observer = new MutationObserver(bindRows);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  async function readCart() {
    const response = await originalFetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(20000) });
    if (!response.ok) throw Error(FAILURE);
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw Error(FAILURE);
    return cart;
  }
  function enqueue(work) {
    pending++;
    const next = queue.then(() => window.navigator.locks ? window.navigator.locks.request(LOCK, work) : work());
    queue = next.catch(() => {}).finally(() => { pending--; });
    return next;
  }
  function sectionTargets() {
    const targets = [];
    for (const [id, selector] of [['main-cart-items', '.js-contents'], ['main-cart-footer', '.js-contents']]) {
      const node = document.getElementById(id);
      if (node?.dataset.id) targets.push({ id, selector, section: node.dataset.id });
    }
    if (document.getElementById('CartDrawer')) targets.push({ id: 'CartDrawer', selector: '.drawer__inner', section: 'cart-drawer' });
    if (document.getElementById('cart-icon-bubble')) targets.push({ id: 'cart-icon-bubble', selector: '.shopify-section', section: 'cart-icon-bubble' });
    if (document.getElementById('cart-live-region-text')) targets.push({ id: 'cart-live-region-text', selector: '.shopify-section', section: 'cart-live-region-text' });
    return targets;
  }
  function render(cart, targets) {
    const changes = targets.map(target => {
      const html = cart.sections?.[target.section];
      const parsed = typeof html === 'string' && new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed && parsed.querySelector(target.selector);
      const host = document.getElementById(target.id);
      if (!fresh || !host) throw Error(FAILURE);
      return { host: host.querySelector(target.selector) || host, html: fresh.innerHTML };
    });
    // Validate all sections before showing any new totals.
    for (const change of changes) change.host.innerHTML = change.html;
    for (const node of document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer, #main-cart-footer')) node.classList.toggle('is-empty', cart.item_count === 0);
    remember(cart);
  }
  function pairedUpdates(cart, target, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 1000) throw Error(FAILURE);
    const matches = cart.items.filter(item => same(item, target));
    // A concurrent removal must not turn an old edit into a new addition.
    if (!matches.length && quantity === 0) return { updates: {}, expected: [] };
    if (matches.length !== 1) throw Error(FAILURE);
    const item = matches[0], updates = Object.create(null), expected = [];
    const set = (line, value) => { updates[line.key] = value; expected.push({ target: identity(line), quantity: value }); };
    if (managed(item)) {
      const group = groups(cart).get(groupKey(item));
      if (item.properties._component === 'wallpaper') {
        set(item, quantity);
        if (group.installation) set(group.installation, quantity);
      } else {
        // Positive fixing edits cannot change the wall or undercharge fixing.
        set(item, quantity === 0 ? 0 : group.wallpaper?.quantity || 0);
      }
    } else set(item, quantity);
    return { updates, expected };
  }
  function matchesExpected(cart, expected) {
    return expected.every(({ target, quantity }) => {
      const found = cart.items.filter(item => same(item, target));
      return quantity === 0 ? found.length === 0 : found.length === 1 && found[0].quantity === quantity;
    });
  }
  async function getSections(cart, sections) {
    if (!sections.length) return { ...cart, sections: {} };
    const url = new URL(`${root}cart`, window.location.origin); url.searchParams.set('sections', sections.join(','));
    const response = await originalFetch(url.pathname + url.search, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(20000) });
    if (!response.ok) throw Error(FAILURE);
    return { ...cart, sections: await response.json() };
  }
  async function writeUpdates(cart, plan, sections, sectionsUrl) {
    if (!Object.keys(plan.updates).length) return getSections(cart, sections);
    let updated;
    try {
      const response = await originalFetch(`${root}cart/update.js`, { method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: plan.updates, sections, sections_url: sectionsUrl }), signal: AbortSignal.timeout(30000) });
      if (!response.ok) throw Error(FAILURE);
      updated = await response.json();
      if (!Array.isArray(updated.items) || updated.errors || !matchesExpected(updated, plan.expected)) throw Error(FAILURE);
    } catch {
      // An uncertain write is read back, never blindly replayed.
      updated = await readCart();
      if (!matchesExpected(updated, plan.expected)) throw Error(FAILURE);
      return getSections(updated, sections);
    }
    if (sections.some(section => typeof updated.sections?.[section] !== 'string')) updated = await getSections(updated, sections);
    return updated;
  }
  function intentFor(event, line) {
    const row = event?.target?.closest?.('[id^="CartItem-"], [id^="CartDrawer-Item-"]');
    return event?.artehubCartIntent || intents.get(row) || identity(displayedCart?.items?.[Number(line) - 1]);
  }
  customElements.whenDefined('cart-items').then(() => {
    const prototype = customElements.get('cart-items').prototype;
    const update = prototype.updateQuantity;
    if (typeof update !== 'function') return;
    prototype.updateQuantity = function(line, quantity, event, ...args) {
      dispatchIntent = intentFor(event, line);
      try { return update.call(this, line, quantity, event, ...args); }
      finally { dispatchIntent = undefined; }
    };
  });
  // Capture the row identity before Dawn's debounced handler or another removal
  // can shift line positions. Flush native validation immediately in mixed carts.
  document.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches?.('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')) return;
    event.artehubCartIntent = intentFor(event, input.dataset.index);
    if (!hasManaged() && initialState === 'ready') return;
    if (checkoutPending) { event.stopImmediatePropagation(); input.value = input.getAttribute('value'); return; }
    const owner = input.closest('cart-items, cart-drawer-items');
    if (typeof owner.validateQuantity === 'function') { event.stopImmediatePropagation(); owner.validateQuantity(event); }
  }, true);
  document.addEventListener('click', event => {
    const remove = event.target.closest?.('cart-remove-button');
    if (checkoutPending && event.target.closest?.('cart-remove-button, quantity-input button')) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (remove) event.artehubCartIntent = intentFor(event, remove.dataset.index);
  }, true);
  async function changeBody(input, options) {
    let value = options.body;
    if (value === undefined && typeof input?.clone === 'function') value = await input.clone().text();
    if (typeof value === 'string') {
      try { return JSON.parse(value); }
      catch { value = new URLSearchParams(value); }
    }
    if (value instanceof URLSearchParams || value instanceof FormData) {
      const body = Object.fromEntries(value.entries());
      if (/^\d+$/.test(body.quantity || '')) body.quantity = Number(body.quantity);
      return body;
    }
    throw Error(FAILURE);
  }
  window.fetch = function(input, options = {}) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
    const method = String(options.method || input?.method || 'GET').toUpperCase();
    if (url.origin !== window.location.origin || method !== 'POST' || ![`${root}cart/change.js`, `${root}cart/change`].includes(url.pathname) ||
        !(hasManaged() || pending || cartFailure || initialState !== 'ready')) return originalFetch(input, options);
    const captured = dispatchIntent;
    const snapshotReady = displayedCart ? Promise.resolve(displayedCart) : initialReady.then(() => displayedCart);
    return enqueue(async () => {
      try {
        await initialReady;
        if (initialState !== 'ready') throw Error(FAILURE);
        const snapshot = await snapshotReady;
        if (!snapshot?.items?.some(managed) && !cartFailure) return originalFetch(input, options);
        const body = await changeBody(input, options);
        // This adapter owns quantity-only theme edits. Other line mutations must
        // not silently bypass pairing while managed configurations are present.
        if (!Number.isSafeInteger(body.quantity) || body.properties || body.selling_plan !== undefined) throw Error(FAILURE);
        const target = captured || (body.line ? identity(snapshot?.items?.[Number(body.line) - 1]) : identity(snapshot?.items?.find(item => item.key === body.id)));
        const requested = Array.isArray(body.sections) ? body.sections : typeof body.sections === 'string' ? body.sections.split(',') : [];
        const targets = sectionTargets();
        const sections = [...new Set([...requested, ...targets.map(target => target.section)])];
        if (!target || sections.length > 5) throw Error(FAILURE);
        const cart = await readCart(), plan = pairedUpdates(cart, target, body.quantity);
        const updated = await writeUpdates(cart, plan, sections, window.location.pathname);
        // Native Dawn still renders its own requested sections. Keep the other
        // surface current as well without altering its cart/checkout mechanism.
        render(updated, targets.filter(target => !requested.includes(target.section)));
        remember(updated); clearFailure(); ++drawerUpdate; ++headerUpdate;
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.dataset.artehubNeedsRefresh = '';
        return new Response(JSON.stringify(updated), { status: 200, headers: { 'Content-Type': 'application/json' } });
      } catch (error) { announceFailure(); throw error; }
    });
  };
  async function checkout() {
    if (checkoutPending) return;
    checkoutPending = true;
    try {
      await enqueue(async () => {
        await initialReady;
        if (initialState !== 'ready' || cartFailure) throw Error(FAILURE);
        const cart = await readCart(), updates = Object.create(null), expected = [];
        for (const group of groups(cart).values()) {
          if (!group.installation) continue; // Explicit opt-out stays removed.
          const quantity = group.wallpaper?.quantity || 0;
          if (group.installation.quantity !== quantity) {
            updates[group.installation.key] = quantity;
            expected.push({ target: identity(group.installation), quantity });
          }
        }
        if (Object.keys(updates).length) {
          const targets = sectionTargets(), sections = [...new Set(targets.map(target => target.section))];
          const updated = await writeUpdates(cart, { updates, expected }, sections, window.location.pathname);
          render(updated, targets);
        } else remember(cart);
        if (cartFailure) throw Error(FAILURE);
        window.location.assign(`${root}checkout`);
      });
    } catch { announceFailure(); }
    finally { checkoutPending = false; }
  }
  document.addEventListener('click', event => {
    if (!(hasManaged() || pending || cartFailure || initialState !== 'ready')) return;
    const button = event.target.closest?.('[name="checkout"], a[href]');
    if (!button) return;
    const href = button.getAttribute('href');
    if (button.name !== 'checkout' && (!href || new URL(href, window.location.href).origin !== window.location.origin ||
        !/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?checkout\/?$/.test(new URL(href, window.location.href).pathname))) return;
    event.preventDefault(); event.stopImmediatePropagation(); checkout();
  }, true);
  document.addEventListener('submit', event => {
    if (!(hasManaged() || pending || cartFailure || initialState !== 'ready')) return;
    const form = event.target;
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation(); checkout();
  }, true);

  function paintHeader(cart) {
      const count = cart.item_count;
      if (!Number.isSafeInteger(count) || count < 0) return;
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon) return;
      let bubble = icon.querySelector('.cart-count-bubble');
      if (count > 0 && !bubble) {
        bubble = document.createElement('div'); bubble.className = 'cart-count-bubble';
        const visible = document.createElement('span'); visible.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span'); label.className = 'visually-hidden';
        bubble.append(visible, label); icon.append(bubble);
      }
      if (bubble) {
        bubble.hidden = count === 0;
        const visible = bubble.querySelector('[aria-hidden]');
        const label = bubble.querySelector('.visually-hidden');
        if (visible) visible.textContent = String(count);
        if (label) label.textContent = String(count);
      }
  }
  async function refresh() {
    const revision = ++headerUpdate;
    try {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(20000) });
      if (!response.ok) return;
      const cart = await response.json();
      if (Array.isArray(cart.items) && Number.isSafeInteger(cart.item_count) && cart.item_count >= 0 && initialState !== 'ready') { remember(cart); initialState = 'ready'; resolveInitial(); }
      if (revision !== headerUpdate) return;
      remember(cart);
      paintHeader(cart);
    } catch { }
    finally { if (initialState === 'pending') { initialState = 'failed'; resolveInitial(); } }
  }
  async function refreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    const target = drawer?.querySelector('#CartDrawer');
    if (!drawer || !target) return;
    const revision = ++drawerUpdate;
    drawer.dataset.artehubNeedsRefresh = 'true';
    try {
      await enqueue(async () => {
        // The matching model and HTML must be read under the same cart lock.
        // A fast drawer render must never inherit the previous cart's indexes.
        const cart = await readCart();
        const response = await originalFetch(`${root}cart?section_id=cart-drawer`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(20000) });
        if (!response.ok) throw Error('Drawer refresh unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = parsed.querySelector('#CartDrawer');
        if (revision !== drawerUpdate) return;
        if (!fresh) throw Error('Drawer section missing');
        displayedCart = cart;
        target.innerHTML = fresh.innerHTML;
        remember(cart); ++headerUpdate; paintHeader(cart);
        drawer.classList.toggle('is-empty', parsed.querySelector('cart-drawer')?.classList.contains('is-empty') || false);
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
        drawer.dataset.artehubNeedsRefresh = '';
      });
    } catch { if (revision === drawerUpdate) drawer.dataset.artehubNeedsRefresh = 'true'; }
  }
  customElements.whenDefined('cart-drawer').then(() => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    const open = drawer.open.bind(drawer);
    drawer.open = (...args) => {
      // If a section refresh failed, the normal full cart is safer than showing
      // an outdated drawer with a missing custom design.
      if (drawer.dataset.artehubNeedsRefresh === 'true') window.location.assign(`${root}cart`);
      else open(...args);
    };
  });
  const schedule = () => { clearTimeout(refreshTimer); refreshTimer = setTimeout(refresh, 100); };
  window.addEventListener('pageshow', schedule);
  window.addEventListener('message', event => {
    if (event.origin !== window.location.origin) return;
    const source = [...document.querySelectorAll('iframe[data-artehub-frame]')].find(frame => frame.contentWindow === event.source);
    if (!source) return;
    if (event.data?.type === 'artehub-native-cart-change') { schedule(); refreshDrawer(); }
    if (event.data?.type === 'artehub-continue-shopping') source.closest('dialog')?.close();
  });
  refresh();
})();
